/**
 * TenantWorkspacePage — the one owner-screen for a tenant (P1 of the
 * console redesign, docs/ADMIN_CONSOLE_REDESIGN.html §5).
 *
 * Route: /:tenantSlug/super-admin/tenants/:tenantId/:section?
 * Sections: overview · modules · models · access · branding ·
 *           integrations · activity
 *
 * Every per-tenant panel mounts here EXACTLY ONCE in the whole app:
 *   modules      → TenantFeaturesPanel (banners)
 *   models       → ModelFamiliesPanel + MetaAgentModelsPanel +
 *                  InternalModelsPanel + ApiKeysPanel
 *   access       → CompanyTenantToggle + MultiUserLoginToggle
 *   branding     → WhiteLabelEditor
 *   integrations → WikiConfigPanel
 *   activity     → door to the Tool Activity page
 *
 * The Overview tab is read-only: it answers "what is different about this
 * customer?" with a diff-vs-platform table, and routes to the tab that owns
 * each setting. The ⌘K tenant switcher preserves the active section across
 * switches.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Button, Modal, Spin, Tag, message } from 'antd';
import api from '@/services/api';
import { useLanguage } from '@/hooks/useLanguage';
import { useTenantPath } from '@/routing/paths';
import superAdminService from '@modules/super_admin/services/superAdminService';
import type { Tenant } from '@modules/super_admin/services/superAdminService';
import { TenantFeaturesPanel } from '@modules/super_admin/components/TenantFeaturesPanel';
import { ModelFamiliesPanel } from '@modules/super_admin/components/ModelFamiliesPanel';
import { MetaAgentModelsPanel } from '@modules/super_admin/components/MetaAgentModelsPanel';
import { InternalModelsPanel } from '@modules/super_admin/components/InternalModelsPanel';
import { ApiKeysPanel } from '@modules/super_admin/components/ApiKeysPanel';
import WikiConfigPanel from '@modules/super_admin/components/WikiConfigPanel';
import { ToolActivityPanel } from '@modules/observability/pages/ToolActivityPage';
import TenantAccessPanel from '@modules/super_admin/components/TenantAccessPanel';
import WhiteLabelEditor, { type EditorView } from '@modules/super_admin/design_system/WhiteLabelEditor';
import SuperAdminShell from '@modules/super_admin/console/SuperAdminShell';
import TenantContextBar from '@modules/super_admin/console/TenantContextBar';
import TenantSwitcher, { useTenantSwitcherHotkey, type SwitcherTenant } from '@modules/super_admin/console/TenantSwitcher';
import {
  isWorkspaceSection,
  tenantOwnLogo,
  pushRecentTenant,
  setLastWorkspaceSection,
  type WorkspaceSection,
} from '@modules/super_admin/console/consoleState';
import '@modules/super_admin/console/console.css';
import './SuperAdminPage.css';

interface RegistryEntry {
  label: string;
  description?: string;
  default: boolean;
  scope: 'tenant' | 'user';
  parent?: string | null;
}

interface FamilySummary {
  key: string;
  name: string;
  enabled: boolean;
  platform_enabled: boolean;
  version: string;
  version_name: string;
  platform_default_version: string;
  is_version_override: boolean;
  is_enabled_override: boolean;
}

/** One row of the "differs from platform" table on the Overview tab. */
interface DiffRow {
  key: string;
  label: string;
  platform: string;
  tenant: string;
  source: 'override' | 'enforced';
  sourceName?: string;
  section: WorkspaceSection;
}

export default function TenantWorkspacePage() {
  const { tenantId, section: sectionParam } = useParams<{ tenantId: string; section?: string }>();
  const navigate = useNavigate();
  const tenantPath = useTenantPath();
  const { t } = useLanguage();

  const section: WorkspaceSection = isWorkspaceSection(sectionParam) ? sectionParam : 'overview';

  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [loading, setLoading] = useState(true);
  const [switcherOpen, setSwitcherOpen] = useTenantSwitcherHotkey();
  // WL editor sub-view (edit / presets / save-as) hoisted here so the
  // Branding tab can offer a Back button for the gallery flows — the
  // editor itself deliberately ships no Back (single Back per page).
  const [brandingView, setBrandingView] = useState<EditorView>('edit');
  // Reported up by the activity panel so the card header can state the total
  // without the panel needing a header of its own.
  const [activityTotal, setActivityTotal] = useState<number | null>(null);

  /** The three questions the Models tab answers, kept apart so each one is a
   *  page rather than a third of a very long one. */
  const MODEL_SECTIONS = [
    { id: 'families' as const, labelKey: 'superAdmin.workspace.modelsSectionFamilies' },
    { id: 'assignments' as const, labelKey: 'superAdmin.workspace.modelsSectionAssignments' },
    // Internal services were bundled with the meta-agents, which put two
    // unrelated concerns — what a user talks to, and what the platform runs
    // in the background — in one 2.6-screen section. They are separate
    // questions and now separate sections.
    { id: 'internal' as const, labelKey: 'superAdmin.workspace.modelsSectionInternal' },
    { id: 'keys' as const, labelKey: 'superAdmin.workspace.modelsSectionKeys' },
  ];
  type ModelSection = (typeof MODEL_SECTIONS)[number]['id'];
  const [modelSection, setModelSection] = useState<ModelSection>('families');

  // Overview data — loaded once per tenant; the editing tabs own their own
  // fetches (the panels are self-contained), so this is read-only state.
  const [registry, setRegistry] = useState<Record<string, RegistryEntry>>({});
  const [featureOverrides, setFeatureOverrides] = useState<Record<string, boolean>>({});
  const [featureCascade, setFeatureCascade] = useState<{ enforced: boolean; parent_enforced: boolean; parent: { tenant_name: string } | null } | null>(null);
  const [families, setFamilies] = useState<FamilySummary[]>([]);
  const [brandOverrides, setBrandOverrides] = useState<Record<string, unknown>>({});

  const tenant = useMemo(
    () => tenants.find(x => x.tenant_id === tenantId),
    [tenants, tenantId],
  );

  const loadTenants = useCallback(async () => {
    try {
      const res = await superAdminService.getTenants({ per_page: 1000 });
      setTenants(res.tenants);
    } catch (err) {
      console.error('[ERROR] Failed to load tenants:', err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadTenants(); }, [loadTenants]);

  // Overview fetches — refreshed on tenant change AND on section change
  // back to overview (edits made on other tabs must show up here).
  useEffect(() => {
    if (!tenantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [reg, feats, fams, brand] = await Promise.all([
          api.get('/super-admin/features/registry'),
          api.get(`/super-admin/tenants/${tenantId}/features`),
          api.get(`/super-admin/tenants/${tenantId}/model-families`),
          api.get(`/super-admin/tenants/${tenantId}/brand-overrides`),
        ]);
        if (cancelled) return;
        setRegistry(reg.data?.registry || {});
        setFeatureOverrides(feats.data?.overrides || {});
        setFeatureCascade(feats.data?.cascade || null);
        setFamilies(fams.data?.families || []);
        setBrandOverrides(brand.data?.brand_overrides || {});
      } catch (err) {
        console.error('[ERROR] Failed to load workspace overview:', err);
      }
    })();
    return () => { cancelled = true; };
  }, [tenantId, section === 'overview']);

  // Record the visit + remember the section for the "switch preserves place"
  // contract.
  useEffect(() => {
    if (tenant) {
      pushRecentTenant({
        tenantId: tenant.tenant_id,
        tenantName: tenant.tenant_name,
        tenantCode: tenant.tenant_code,
      });
    }
  }, [tenant?.tenant_id]);

  useEffect(() => { setLastWorkspaceSection(section); }, [section]);

  const goSection = useCallback((s: WorkspaceSection) => {
    navigate(tenantPath(`/super-admin/tenants/${tenantId}/${s}`));
  }, [navigate, tenantPath, tenantId]);

  const switchTenant = useCallback((newTenantId: string) => {
    // Same section, different tenant — the whole point of the switcher.
    navigate(tenantPath(`/super-admin/tenants/${newTenantId}/${section}`));
  }, [navigate, tenantPath, section]);

  /* ─── Overview derivations ─────────────────────────────────────────── */

  const moduleStats = useMemo(() => {
    const keys = Object.entries(registry).filter(([, m]) => m.scope === 'tenant');
    const resolvedOn = keys.filter(([k, m]) =>
      (k in featureOverrides ? !!featureOverrides[k] : !!m.default)).length;
    const overridden = Object.keys(featureOverrides).length;
    return { total: keys.length, on: resolvedOn, overridden };
  }, [registry, featureOverrides]);

  const modelStats = useMemo(() => {
    const enabled = families.filter(f => f.enabled).length;
    const overridden = families.filter(f => f.is_version_override || f.is_enabled_override).length;
    return { total: families.length, enabled, overridden };
  }, [families]);

  const brandFieldCount = Object.keys(brandOverrides).length;

  const diffRows = useMemo<DiffRow[]>(() => {
    const rows: DiffRow[] = [];
    const enforcedByParent = !!featureCascade?.parent_enforced;
    const parentName = featureCascade?.parent?.tenant_name;
    for (const [key, value] of Object.entries(featureOverrides)) {
      const meta = registry[key];
      if (!meta) continue;
      if (!!value === !!meta.default) continue; // stored but not a real diff
      rows.push({
        key,
        label: meta.label,
        platform: meta.default
          ? t('superAdmin.workspace.on')
          : t('superAdmin.workspace.off'),
        tenant: value
          ? t('superAdmin.workspace.on')
          : t('superAdmin.workspace.off'),
        source: enforcedByParent ? 'enforced' : 'override',
        sourceName: enforcedByParent ? parentName : undefined,
        section: 'modules',
      });
    }
    for (const f of families) {
      if (f.is_version_override) {
        rows.push({
          key: `models.${f.key}.version`,
          label: `${f.name} — ${t('superAdmin.workspace.pinnedRelease')}`,
          platform: f.platform_default_version,
          tenant: f.version_name || f.version,
          source: 'override',
          section: 'models',
        });
      }
      if (f.is_enabled_override) {
        rows.push({
          key: `models.${f.key}.enabled`,
          label: f.name,
          platform: f.platform_enabled
            ? t('superAdmin.workspace.on')
            : t('superAdmin.workspace.off'),
          tenant: f.enabled
            ? t('superAdmin.workspace.on')
            : t('superAdmin.workspace.off'),
          source: 'override',
          section: 'models',
        });
      }
    }
    if (brandFieldCount > 0) {
      rows.push({
        key: 'brand',
        label: t('superAdmin.workspace.brandOverrides'),
        platform: t('superAdmin.workspace.baseBrand'),
        tenant: t('superAdmin.workspace.brandFieldsEdited', { count: String(brandFieldCount) })
          || `${brandFieldCount} fields`,
        source: 'override',
        section: 'branding',
      });
    }
    return rows;
  }, [featureOverrides, registry, families, brandFieldCount, featureCascade, t]);

  /* ─── Render ───────────────────────────────────────────────────────── */

  if (!tenantId) {
    return <div style={{ padding: 'var(--space-6)' }}>{t('superAdmin.workspace.noTenant')}</div>;
  }

  const SECTIONS: Array<{ id: WorkspaceSection; label: string; badge?: number }> = [
    { id: 'overview', label: t('superAdmin.workspace.tabOverview') },
    { id: 'modules', label: t('superAdmin.workspace.tabModules'), badge: moduleStats.overridden || undefined },
    { id: 'models', label: t('superAdmin.workspace.tabModels'), badge: modelStats.overridden || undefined },
    { id: 'access', label: t('superAdmin.workspace.tabAccess') },
    { id: 'branding', label: t('superAdmin.workspace.tabBranding'), badge: brandFieldCount || undefined },
    { id: 'integrations', label: t('superAdmin.workspace.tabIntegrations') },
    { id: 'activity', label: t('superAdmin.workspace.tabActivity') },
  ];

  const switcherTenants: SwitcherTenant[] = tenants.map(x => ({
    tenant_id: x.tenant_id,
    tenant_name: x.tenant_name,
    tenant_code: x.tenant_code,
    parent_tenant_id: x.parent_tenant_id ?? null,
    user_count: x.user_count,
    logo: tenantOwnLogo(x),
  }));

  // Delete lives WITH the tenant, on its Overview. The backend refuses a root
  // tenant, a tenant with sub-tenants, or one with users — that message is
  // surfaced verbatim so the operator knows what to move first.
  const handleDelete = () => {
    if (!tenant) return;
    Modal.confirm({
      title: t('superAdmin.workspace.dangerTitle'),
      content: t('superAdmin.workspace.deleteConfirm').replace('{{name}}', tenant.tenant_name),
      okText: t('superAdmin.workspace.deleteButton'),
      okButtonProps: { danger: true },
      cancelText: t('common.cancel'),
      onOk: async () => {
        try {
          await superAdminService.deleteTenant(tenant.tenant_id);
          message.success(t('superAdmin.tenants.deleteSuccess'));
          navigate(tenantPath('/super-admin'));
        } catch (error) {
          const detail = (error as { response?: { data?: { error?: string } } })?.response?.data?.error;
          message.error(detail || t('superAdmin.tenants.deleteFailed'));
        }
      },
    });
  };

  // Everything that must NOT move while the content scrolls: Back (always
  // in this exact spot), the tenant context bar, and the section tabs.
  const chrome = (
    <div className="sa-chrome">
      <div className="sa-chrome-row">
        <button
          type="button"
          className="sa-back"
          onClick={() => navigate(tenantPath('/super-admin'))}
          data-testid="workspace-back"
        >
          <svg className="sa-back-icon" viewBox="0 0 24 24" aria-hidden>
            <path d="M15 18l-6-6 6-6" />
          </svg>
          {t('superAdmin.workspace.backToTenants')}
        </button>
        {tenant && (
          <TenantContextBar
            tenant={{
              tenant_id: tenant.tenant_id,
              tenant_name: tenant.tenant_name,
              tenant_code: tenant.tenant_code,
              parent_tenant_id: tenant.parent_tenant_id ?? null,
              logo: tenantOwnLogo(tenant),
            }}
            allTenants={switcherTenants}
            tags={
              <>
                {tenant.is_company && <Tag color="blue">{t('superAdmin.workspace.companyTag')}</Tag>}
                {featureCascade?.parent_enforced && featureCascade.parent && (
                  <Tag color="orange">
                    {t('superAdmin.workspace.enforcedBy', { name: featureCascade.parent.tenant_name })}
                  </Tag>
                )}
              </>
            }
            onOpenSwitcher={() => setSwitcherOpen(true)}
          />
        )}
      </div>
      {tenant && (
        <nav className="sa-subnav" aria-label={t('superAdmin.workspace.sectionsNav')}>
          {SECTIONS.map(s => (
            <button
              key={s.id}
              type="button"
              className={`sa-subnav-item ${section === s.id ? 'sa-subnav-item--active' : ''}`}
              onClick={() => goSection(s.id)}
              data-testid={`workspace-tab-${s.id}`}
            >
              {s.label}
              {s.badge !== undefined && <span className="sa-subnav-badge">{s.badge}</span>}
            </button>
          ))}
        </nav>
      )}
    </div>
  );

  return (
    <SuperAdminShell active="tenants" chrome={chrome} fill={section === 'branding' && brandingView === 'edit'}>
          {loading || !tenant ? (
            <div style={{ padding: 'var(--space-8)', textAlign: 'center' }}>
              {loading ? <Spin /> : t('superAdmin.workspace.tenantNotFound')}
            </div>
          ) : (
            <div className="sa-workspace" data-testid="tenant-workspace">

              {/* ── Overview ─────────────────────────────────────────── */}
              {section === 'overview' && (
                <div data-testid="workspace-overview">
                  <div className="sa-overview-cards">
                    <div className="sa-overview-card">
                      <div className="sa-overview-card-label">{t('superAdmin.workspace.tabModules')}</div>
                      <div className="sa-overview-card-value">
                        {moduleStats.on} <small>{t('superAdmin.workspace.ofNOn', { total: String(moduleStats.total) })}</small>
                      </div>
                      <div className="sa-overview-card-meta">
                        {t('superAdmin.workspace.nOverridden', { count: String(moduleStats.overridden) })}
                      </div>
                    </div>
                    <div className="sa-overview-card">
                      <div className="sa-overview-card-label">{t('superAdmin.workspace.tabModels')}</div>
                      <div className="sa-overview-card-value">
                        {modelStats.enabled} <small>{t('superAdmin.workspace.familiesOn')}</small>
                      </div>
                      <div className="sa-overview-card-meta">
                        {t('superAdmin.workspace.nOverridden', { count: String(modelStats.overridden) })}
                      </div>
                    </div>
                    <div className="sa-overview-card">
                      <div className="sa-overview-card-label">{t('superAdmin.workspace.tabBranding')}</div>
                      <div className="sa-overview-card-value">
                        {brandFieldCount > 0
                          ? <>{brandFieldCount} <small>{t('superAdmin.workspace.brandFields')}</small></>
                          : t('superAdmin.workspace.baseBrand')}
                      </div>
                      <div className="sa-overview-card-meta">
                        {t('superAdmin.workspace.usersCount', { count: String(tenant.user_count ?? 0) })}
                      </div>
                    </div>
                  </div>

                  <div className="card">
                    <div className="card-header">
                      <h3>{t('superAdmin.workspace.diffTitle')}</h3>
                      <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                        {t('superAdmin.workspace.diffSubtitle')}
                      </p>
                    </div>
                    <div className="card-content">
                      {diffRows.length === 0 ? (
                        <p className="text-muted" style={{ padding: 'var(--space-4)', margin: 0 }}>
                          {t('superAdmin.workspace.noDiffs')}
                        </p>
                      ) : (
                        <table className="sa-diff-table" data-testid="workspace-diff-table">
                          <thead>
                            <tr>
                              <th>{t('superAdmin.workspace.colSetting')}</th>
                              <th>{t('superAdmin.workspace.colPlatform')}</th>
                              <th>{t('superAdmin.workspace.colTenant')}</th>
                              <th>{t('superAdmin.workspace.colSource')}</th>
                              <th />
                            </tr>
                          </thead>
                          <tbody>
                            {diffRows.map(row => (
                              <tr key={row.key}>
                                <td><span className="sa-diff-key" dir="auto">{row.label}</span></td>
                                <td>{row.platform}</td>
                                <td>{row.tenant}</td>
                                <td>
                                  <span className={`sa-state-chip sa-state-chip--${row.source === 'enforced' ? 'enforced' : 'override'}`}>
                                    {row.source === 'enforced'
                                      ? t('superAdmin.workspace.enforcedBy', { name: row.sourceName || '' })
                                      : t('superAdmin.workspace.tenantOverride')}
                                  </span>
                                </td>
                                <td>
                                  <Button size="small" onClick={() => goSection(row.section)}>
                                    {t('superAdmin.workspace.go')}
                                  </Button>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>

                  <div className="sa-danger-card" data-testid="workspace-danger">
                    <div className="sa-danger-text">
                      <p className="sa-danger-title">{t('superAdmin.workspace.dangerTitle')}</p>
                      <p className="sa-danger-sub">{t('superAdmin.workspace.dangerSub')}</p>
                    </div>
                    <Button danger onClick={handleDelete} data-testid="workspace-delete-tenant">
                      {t('superAdmin.workspace.deleteButton')}
                    </Button>
                  </div>
                </div>
              )}

              {/* ── Modules ──────────────────────────────────────────── */}
              {section === 'modules' && (
                <div data-testid="workspace-modules">
                  <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                    {t('superAdmin.workspace.modulesSubtitle')}
                  </p>
                  {/* The rows variant brings its own Card + cascade toolbar. */}
                  <TenantFeaturesPanel key={tenantId} tenantId={tenantId} />
                </div>
              )}

              {/* ── Models ───────────────────────────────────────────── */}
              {section === 'models' && (
                <div className="sa-models" data-testid="workspace-models">
                  {/* One concern at a time. Mounting all four panels at once
                      is what made this tab an endless scroll — and each one
                      fetches, so it was also four round trips to look at one
                      of them. */}
                  <nav className="sa-subtabs" role="tablist" aria-label={t('superAdmin.workspace.tabModels')}>
                    {MODEL_SECTIONS.map(ms => (
                      <button
                        key={ms.id}
                        type="button"
                        role="tab"
                        aria-selected={modelSection === ms.id}
                        className={`sa-subtab ${modelSection === ms.id ? 'sa-subtab--active' : ''}`}
                        onClick={() => setModelSection(ms.id)}
                        data-testid={`models-subtab-${ms.id}`}
                      >
                        {t(ms.labelKey)}
                        {ms.id === 'families' && modelStats.overridden > 0 && (
                          <span className="sa-subtab-badge">{modelStats.overridden}</span>
                        )}
                      </button>
                    ))}
                  </nav>

                  {modelSection === 'families' && (
                    <section className="sa-panel" data-testid="models-section-families">
                      <header className="sa-panel-head">
                        <h3>{t('superAdmin.modelFamilies.cardTitle') || 'AI Models'}</h3>
                        <p>{t('superAdmin.modelFamilies.treePaneDescription')}</p>
                      </header>
                      <div className="sa-models-body">
                        <ModelFamiliesPanel key={`fam-${tenantId}`} tenantId={tenantId} bare />
                      </div>
                    </section>
                  )}

                  {modelSection === 'assignments' && (
                    <section className="sa-panel" data-testid="models-section-assignments">
                      <header className="sa-panel-head">
                        <h3>{t('superAdmin.workspace.assignmentsTitle')}</h3>
                        <p>{t('superAdmin.workspace.assignmentsSubtitle')}</p>
                      </header>
                      <div className="sa-models-body">
                        <MetaAgentModelsPanel key={`meta-${tenantId}`} tenantId={tenantId} />
                      </div>
                    </section>
                  )}

                  {modelSection === 'internal' && (
                    <section className="sa-panel" data-testid="models-section-internal">
                      <header className="sa-panel-head">
                        <h3>{t('superAdmin.workspace.internalTitle')}</h3>
                        <p>{t('superAdmin.workspace.internalSubtitle')}</p>
                      </header>
                      <div className="sa-models-body">
                        <InternalModelsPanel key={`int-${tenantId}`} tenantId={tenantId} />
                      </div>
                    </section>
                  )}

                  {modelSection === 'keys' && (
                    <section className="sa-panel" data-testid="models-section-keys">
                      <header className="sa-panel-head">
                        <h3>{t('superAdmin.workspace.keysTitle')}</h3>
                        <p>{t('superAdmin.workspace.keysSubtitle')}</p>
                      </header>
                      <div className="sa-models-body">
                        <ApiKeysPanel key={`keys-${tenantId}`} tenantId={tenantId} />
                      </div>
                    </section>
                  )}
                </div>
              )}

              {/* ── Access & login — addresses and sign-in policy ───── */}
              {section === 'access' && (
                <TenantAccessPanel
                  tenant={tenant}
                  onChanged={(updated) =>
                    setTenants(prev => prev.map(x => x.tenant_id === updated.tenant_id ? { ...x, ...updated } : x))}
                />
              )}

              {/* ── Branding — the White-Label editor, owned here ─────── */}
              {section === 'branding' && (
                <div data-testid="workspace-branding"
                     className={brandingView === 'edit' ? 'wl-editor-host sa-branding-host' : undefined}>
                  {brandingView !== 'edit' && (
                    <Button
                      type="text"
                      className="ds-back-button"
                      onClick={() => setBrandingView('edit')}
                      data-testid="branding-substep-back"
                    >
                      ← {t('superAdmin.workspace.backToEditor')}
                    </Button>
                  )}
                  <WhiteLabelEditor
                    tenantId={tenantId}
                    key={tenantId}
                    view={brandingView}
                    onViewChange={setBrandingView}
                  />
                </div>
              )}

              {/* ── Integrations ─────────────────────────────────────── */}
              {section === 'integrations' && (
                <div className="card" data-testid="workspace-integrations">
                  <div className="card-header">
                    <h3>{t('superAdmin.workspace.integrationsTitle')}</h3>
                    <p className="text-muted" style={{ margin: 0, fontSize: 'var(--text-sm)' }}>
                      {t('superAdmin.workspace.integrationsSubtitle')}
                    </p>
                  </div>
                  <div className="card-content" style={{ paddingTop: 'var(--space-2)' }}>
                    <WikiConfigPanel key={`wiki-${tenantId}`} tenantId={tenantId} />
                  </div>
                </div>
              )}

              {/* ── Activity — the tool log itself, not a door to it ─── */}
              {section === 'activity' && (
                <section className="sa-panel sa-activity-card" data-testid="workspace-activity">
                  <header className="sa-panel-head">
                    <h3>{t('superAdmin.workspace.activityTitle')}</h3>
                    <p>
                      {activityTotal === null
                        ? t('superAdmin.workspace.activitySubtitle')
                        : t('superAdmin.workspace.activityCount').replace('{{count}}', String(activityTotal))}
                    </p>
                  </header>
                  <div className="sa-activity-body">
                    {/* Keyed like every sibling panel: the page stays mounted
                        across a tenant switch, and this panel holds page /
                        perPage / filters in its own state. Without the key,
                        jumping from tenant A (filtered to errors, page 3) to
                        tenant B refetched B with A's filter and page, and the
                        operator saw an empty result that looked like "tenant B
                        has no tool activity". */}
                    <ToolActivityPanel
                      key={`activity-${tenantId}`}
                      tenantId={tenantId}
                      onCount={setActivityTotal}
                    />
                  </div>
                </section>
              )}
            </div>
          )}

          <TenantSwitcher
            tenants={switcherTenants}
            currentTenantId={tenantId}
            open={switcherOpen}
            onClose={() => setSwitcherOpen(false)}
            onSelect={switchTenant}
          />
    </SuperAdminShell>
  );
}
